import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin } from 'antd'
import { useMemo } from 'react'

import { useClassifierServiceGetCommonClassifierElementListEndpointKey } from 'src/api/queries'
import {
  ClassifierService,
  GetCommonClassifierElementResponse
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Select } from 'src/components'
import { useDefaultSortingItem } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  id?: string
  hasDefault?: boolean
  filterValues?: (string | number)[]
} & SelectProps

export const ClassifierInfiniteSelect = ({
  id,
  hasDefault,
  placeholder,
  filterValues,
  ...rest
}: Props) => {
  const { currentLang } = useLocales()

  const { defaultSortingOption } = useDefaultSortingItem()
  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(id)
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getCommonClassifierElementListEndpoint(
          String(id),
          pageParam,
          DEFAULT_PER_PAGE
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCommonClassifierElementResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!id
  })

  const selectOptions = useMemo(() => {
    let options = [
      ...(hasDefault && defaultSortingOption ? [defaultSortingOption] : []),
      ...(data?.pages.flatMap(
        (page) =>
          page.data?.map((item: GetCommonClassifierElementResponse) => ({
            value: item?.id,
            label: item?.translation?.[currentLang?.value]
          })) || []
      ) || [])
    ]

    if (filterValues && filterValues.length > 0) {
      const filterSet = new Set(filterValues.map(String))
      options = options.filter((option) => !filterSet.has(String(option.value)))
    }

    return options
  }, [hasDefault, data, currentLang, filterValues])

  return (
    <Select
      allowClear
      filterOption={false}
      options={selectOptions}
      loading={isFetching}
      placeholder={placeholder}
      showSearch={false}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...rest}
    />
  )
}
